export const ARCH_FOIL_COLOUR_WIDTH_MIN = 600;
export const SWEPT_ARCH_FOIL_RADIUS_MIN = 400;

export function isProfileColourFoilOption(responseValue: string): boolean {
  return !!responseValue?.toLowerCase().includes("foil");
}

export function isSweptArchProduct(optionText?: string): boolean {
  return !!optionText?.toLowerCase().includes("swept");
}

/**
 * Whether an Arch profile colour option should be visible given the frame width.
 * Standard: always visible (no width restriction).
 * Foil:     width >= 800mm.
 * When width is unknown (0) both are shown.
 */
export function shouldShowArchProfileColourOption(
  responseValue: string,
  width: number,
): boolean {
  if (width <= 0) return true;
  if (isProfileColourFoilOption(responseValue)) {
    return width >= ARCH_FOIL_COLOUR_WIDTH_MIN;
  }
  return true;
}

/**
 * Calculates the swept arch radius from the three dimension fields.
 * Returns null when any required field is missing/zero.
 */
export function calculateSweptArchRadius(
  width: number,
  overallHeight: number,
  shoulderHeight: number,
): { radius: number; archHeight: number } | null {
  if (width <= 0 || overallHeight <= 0 || shoulderHeight <= 0) return null;
  const archHeight = overallHeight - shoulderHeight;
  if (archHeight <= 0) return null;
  const radius =
    ((width * width) / 4 + archHeight * archHeight) / (2 * archHeight);
  return { radius, archHeight };
}

/**
 * Whether a foil profile colour option should be shown for a Swept Arch.
 * Requires all three dimension fields. Returns true (show) when fields are
 * not yet filled so foil stays visible until the user enters dimensions.
 */
export function shouldShowSweptArchFoilOption(
  width: number,
  overallHeight: number,
  shoulderHeight: number,
): boolean {
  const result = calculateSweptArchRadius(width, overallHeight, shoulderHeight);
  if (!result) return true; // not enough data yet — don't hide
  return result.radius >= SWEPT_ARCH_FOIL_RADIUS_MIN;
}

/**
 * Returns an alert string for the swept arch profile colour question, or null
 * when everything is fine / fields are not yet filled.
 *
 * Three possible states:
 *  1. Arch height invalid (shoulder >= overall) → validation error.
 *  2. Radius < threshold → foil unavailable message.
 *  3. Width < 800mm but foil is radius-allowed → POA warning (from width rule).
 */
export function getSweptArchProfileColourAlert(
  width: number,
  overallHeight: number,
  shoulderHeight: number,
): string | null {
  if (width <= 0 || overallHeight <= 0 || shoulderHeight <= 0) return null;

  const archHeight = overallHeight - shoulderHeight;
  if (archHeight <= 0) {
    return "Shoulder height cannot equal or exceed overall height.";
  }

  const radius =
    ((width * width) / 4 + archHeight * archHeight) / (2 * archHeight);

  if (radius < SWEPT_ARCH_FOIL_RADIUS_MIN) {
    return (
      `Foil is not available for this arch configuration. ` +
      `Radius is ${Math.round(radius)}mm, below the minimum of ${SWEPT_ARCH_FOIL_RADIUS_MIN}mm.`
    );
  }

  return null;
}

/**
 * Returns an alert when only Standard colour is available (width < 800mm),
 * or null when both Standard and Foil are available (width >= 800mm).
 */
export function getArchProfileColourWidthAlert(width: number): string | null {
  if (width <= 0) return null;
  if (width < ARCH_FOIL_COLOUR_WIDTH_MIN) {
    return (
      `Foil colour is only available for arch windows ${ARCH_FOIL_COLOUR_WIDTH_MIN}mm or wider. ` +
      `Only Standard colour is available for the current width of ${width}mm.`
    );
  }
  return null;
}
